// BiscuitMini — compact pet display for the profile sidebar or topbar.
//
// Ported from remix-of-activity-tracker. Pet-care navigation, customization,
// remote variant images stripped. Pure visual + mood, rendered with the
// chibi corgi PNGs served from /biscuit/.

import type { BiscuitMood } from '@/lib/biscuit/mood'
import { BiscuitWardrobe } from './BiscuitWardrobe'
import { useEquipped } from '@/lib/wardrobe/use-wardrobe'
import { CATALOG_BY_ID } from '@/lib/wardrobe/catalog'

interface BiscuitMiniProps {
  /** Mood to render. Defaults to "content". */
  mood?: BiscuitMood
  /** Show the internal "Biscuit" + mood label. Defaults to false to avoid double-labeling on wrapped surfaces. */
  showLabel?: boolean
}

const MOOD_LABEL: Record<BiscuitMood, string> = {
  happy:       'Happy',
  content:     'Content',
  sleepy:      'Sleepy',
  worried:     'Worried',
  sad:         'Sad',
  celebrating: 'Celebrating',
}

export function BiscuitMini({ mood = 'content', showLabel = false }: BiscuitMiniProps) {
  const label = MOOD_LABEL[mood]
  const equipped = useEquipped()
  const look = equipped.look ? CATALOG_BY_ID[equipped.look] : null

  return (
    <div className="flex items-center gap-3">
      <div
        className="relative w-20 h-20 flex-shrink-0 flex items-center justify-center"
        aria-label={showLabel ? undefined : `Biscuit is ${label.toLowerCase()}`}
        role={showLabel ? undefined : "img"}
      >
        {/* Soft warm halo instead of a hard square frame — no straight edges, blends
            into whatever surface Biscuit sits on (dashboard card, login circle). */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 42%, hsl(45 85% 97%) 0%, hsl(42 75% 90% / 0.55) 54%, transparent 78%)",
          }}
        />
        {look ? (
          <img
            src={look.image}
            alt=""
            aria-hidden="true"
            className="relative w-16 h-16 sm:w-20 sm:h-20 object-contain"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <>
            <picture>
              <source srcSet={`/biscuit/${mood}-256.avif`} type="image/avif" />
              <source srcSet={`/biscuit/${mood}-256.webp`} type="image/webp" />
              <img
                src={`/biscuit/${mood}.png`}
                alt=""
                aria-hidden="true"
                className="relative w-16 h-16 sm:w-20 sm:h-20 object-contain"
                loading="lazy"
                decoding="async"
              />
            </picture>
            <BiscuitWardrobe equipped={equipped} size="mini" />
          </>
        )}
      </div>
      {showLabel && (
        <div className="min-w-0">
          <div className="text-base font-semibold text-foreground truncate">Biscuit</div>
          <div className="text-sm text-muted-foreground mt-0.5 truncate">{label}</div>
        </div>
      )}
    </div>
  )
}
